'use client';

// TODO fix jsx-a11y
// TODO fix Public API

/* eslint-disable jsx-a11y/no-static-element-interactions,  jsx-a11y/click-events-have-key-events, import/no-internal-modules */
import cn from 'classnames';
import he from 'he';
import Image from 'next/image';
import Link from 'next/link';
import React, { useCallback, useLayoutEffect, useRef, useState, Fragment } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { PhotoProvider, PhotoView } from 'react-photo-view';

// eslint-disable-next-line boundaries/element-types
import { AiPlayer } from '@widgets/ai-player';
// eslint-disable-next-line boundaries/element-types
import { useFavourites } from '@features/favourites';

import { ENV, FeatureToggles } from '@shared/config';
import { Player, getNounForm } from '@shared/libs';
import type { Locale } from '@shared/libs';

import { NotesCardQueryData, Notes, Musician, Questions, Reviews, ReviewRatings } from '@shared/types';
import { Breadcrumbs, H1, Rating, Complexity } from '@shared/ui';
import type { FaqItem } from '@shared/utils';

import { BannerPlayer, BannerVideoLesson } from './banners';
import { BuyForm } from './buy-form';
import Description from './description/description';
import DownloadPreview from './download-preview/download-preview';
import Faq from './faq/faq';
import Guarantees from './guarantees/guarantees';
import styles from './notes-card-content.module.css';
import OtherArrangements from './other-arrangements/other-arrangements';
import { ProductQuestions } from './product-questions';
import { ProductReviews } from './product-reviews';
import RecentlyViewed from './recently-viewed/recently-viewed';
import SimilarMusicians from './similar/similar-musicians';
import Subscribe from './subscribe/subscribe';
import { VideoLesson } from './video-lesson';

type Props = {
  data: NotesCardQueryData['data'];
  locale: Locale;
  otherArrangement: Notes[];
  musiciansArrangement: Notes[];
  similarMusicians: Musician[];
  questions: Questions;
  questionsPager: { page_current: number; pages_all: number; records_all: number; records_per_page: number };
  reviews: Reviews;
  reviewsPager?: { page_current: number; pages_all: number; records_all: number; records_per_page: number };
  reviewRatings: ReviewRatings;
  breadcrumb: {
    title: string;
    url: string;
  }[];
  h1: string;
  path: string;
  faqItems?: FaqItem[];
};

const NotesCardContent = ({
  data,
  locale,
  otherArrangement,
  musiciansArrangement,
  similarMusicians,
  questions,
  questionsPager,
  reviews,
  reviewsPager,
  reviewRatings,
  breadcrumb,
  h1,
  path,
  faqItems,
}: Props) => {
  const { isFavorite, toggleFavorite, countFavorite } = useFavourites({
    id: data?.id,
    count: data?.likes,
    path,
  });

  const similarRef = useRef<HTMLDivElement | null>(null);
  const downloadPdfRef = useRef<HTMLDivElement | null>(null);
  const otherArrangementsRef = useRef<HTMLDivElement | null>(null);
  const questionsRef = useRef<HTMLDivElement | null>(null);
  const reviewsRef = useRef<HTMLDivElement | null>(null);
  const [favButtonText, setFavButtonText] = useState(locale.FAVORITE_OFF);
  const [favStyles, setFavStyles] = useState(styles.favourites);
  const [favLikeStyles, setFavLikeStyles] = useState(styles.likesCounter);

  useLayoutEffect(() => {
    if (isFavorite) {
      setFavButtonText(locale.FAVORITE_ON);
      setFavStyles(cn(styles.favourites, styles.isFav));
      setFavLikeStyles(cn(styles.likesCounter, styles.likesCounterActive));
    } else {
      setFavStyles(styles.favourites);
      setFavLikeStyles(styles.likesCounter);
      setFavButtonText(locale.FAVORITE_OFF);
    }
  }, [isFavorite, locale]);

  const handleScrollToSimilar = useCallback(() => {
    similarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleScrollToDownload = useCallback(() => {
    downloadPdfRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const handleScrollToOtherArrangements = useCallback(() => {
    otherArrangementsRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const handleScrollToQuestions = useCallback(() => {
    questionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleScrollToReviews = useCallback(() => {
    reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleDownloadPreview = useCallback(() => {
    // Log the error to an error reporting service
    if (process.env.NODE_ENV !== 'development') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      window.ym?.(Number(process.env.NEXT_PUBLIC_YA_METRIKA), 'reachGoal', 'DOWNLOAD_PREVIEW');
    }
  }, []);

  if (!data) {
    return <div>Данные не найдены, попробуйте обновить страницу</div>;
  } // TODO: error screen

  const {
    article,
    genres,
    title,
    rating,
    instruments,
    musicians,
    arrangement,
    arrangements,
    img,
    mp3,
    id: orderId,
    views,
    complexity,
    vote_count,
    signatureartist,
    // pdfpages,
  } = data;
  const otherArrangementsIds = arrangements?.map((arr) => arr.offer_id);

  const countOtherArrangement = otherArrangement
    ? otherArrangement.filter((item) => item.instruments).length
    : 0;

  return (
    <>
      <ErrorBoundary fallback={null}>
        <Breadcrumbs
          paths={breadcrumb
            .map(({ title: titleB, url }) => ({ title: titleB, path: url }))
            .slice(0, -1)}
          currentTitle={breadcrumb.at(-1)?.title || ''}
          bgColor="white"
          className={styles.breadcrumbs}
        />
      </ErrorBoundary>
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <ErrorBoundary fallback={null}>
            <div className={styles.notesCard}>
              <ErrorBoundary fallback={null}>
                <div className={styles.mobileOnly}>
                  <div
                    className={cn(styles.counters, styles.countersMobile, {
                      [styles.mobileOnlyComplexity]: complexity,
                    })}
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                    <span className={favLikeStyles} onClick={toggleFavorite}>
                      {countFavorite}
                    </span>
                    <span className={styles.viewsCounter}>{views}</span>
                  </div>
                </div>
              </ErrorBoundary>
              <div className={styles.banners}>
                <div>
                  <BannerPlayer locale={locale} />
                </div>
                <div>
                  <BannerVideoLesson locale={locale} />
                </div>
              </div>
              <div className={styles.h1}>
                <H1 className={styles.title}>{he.decode(h1 || '')}</H1>
              </div>
              <ErrorBoundary fallback={null}>
                <div className={styles.card}>
                  <div className={styles.header}>
                    <div className={styles.cardActions}>
                      <ErrorBoundary fallback={null}>
                        <Rating
                          rate={rating}
                          id={orderId}
                          locale={locale}
                          vote_count={vote_count}
                          path={path}
                        />
                      </ErrorBoundary>

                      <ErrorBoundary fallback={null}>
                        <div className={styles.actionsGroup}>
                          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                          <div className={favStyles} onClick={toggleFavorite}>
                            <span className={styles.favouritesText}>{favButtonText}</span>
                          </div>
                          {FeatureToggles.shareIcon && (
                            <Link href="/" prefetch={false}>
                              <span className={styles.share}>
                                <span className={styles.shareText}>{locale.SHARE}</span>
                              </span>
                            </Link>
                          )}
                        </div>
                      </ErrorBoundary>
                    </div>
                    <ErrorBoundary fallback={null}>
                      <div className={styles.cardInfo}>
                        <div className={cn(styles.counters, styles.desktopAndTabletOnly)}>
                          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                          <span className={favLikeStyles} onClick={toggleFavorite}>
                            {countFavorite}
                          </span>
                          <span className={styles.viewsCounter}>{views}</span>
                        </div>
                        <Complexity complexity={complexity} className={styles.complexity} />
                      </div>
                    </ErrorBoundary>
                  </div>
                </div>
              </ErrorBoundary>

              <div className={styles.cardBody}>
                <div className={styles.cardPreview}>
                  <ErrorBoundary fallback={null}>
                    <PhotoProvider maskOpacity={0.5}>
                      {img?.src && (
                        <PhotoView src={`${ENV.STORAGE}${img?.src}`} width={551} height={780}>
                          <Image
                            src={`${ENV.STORAGE}${img?.src}`}
                            width={595}
                            height={842}
                            alt={h1}
                            className={styles.cardPreviewImg}
                          />
                        </PhotoView>
                      )}
                    </PhotoProvider>
                  </ErrorBoundary>
                  <ErrorBoundary fallback={null}>
                    {data?.isempty === false && (
                      <a
                        href={`${ENV.API}/preview_pdf_download/${orderId}/`}
                        download
                        target="_blank"
                        className={styles.downloadPdf}
                        // href={`${ENV.API}/preview_pdf_download/${orderId}/`}
                        // download
                        onClick={handleDownloadPreview}
                      >
                        {data.DOWNLOAD_PREVIEW_PDF_BACK}
                      </a>
                    )}
                  </ErrorBoundary>
                </div>

                <div className={styles.description}>
                  <ErrorBoundary fallback={null}>
                    <BuyForm locale={locale} data={data} h1={h1} />
                  </ErrorBoundary>
                  <div className={styles.characteristics}>
                    <ErrorBoundary fallback={null}>
                      {musicians && (
                        <div className={styles.char}>
                          <span className={styles.charTitle}>{`${locale.NOTE_ACTOR}:`}</span>
                          {musicians.map((m, index) => (
                            <Fragment key={m.id}>
                              <Link
                                href={m.url}
                                className={cn(styles.charContent, styles.musiciansTitle)}
                                prefetch={false}
                              >
                                {m.title}
                              </Link>
                              {index < musicians.length - 1 ? ', ' : ''}
                            </Fragment>
                          ))}
                        </div>
                      )}
                    </ErrorBoundary>
                    <ErrorBoundary fallback={null}>
                      {genres?.[0]?.title && (
                        <div className={styles.char}>
                          <span className={styles.charTitle}>{`${locale.NOTE_GENRE}:`}</span>
                          {genres.map((g, index) => (
                            <Fragment key={g.code}>
                              <Link
                                href={`/category/genre-is-${g.code}/apply/`}
                                className={styles.charContent}
                                prefetch={false}
                              >
                                {g.title}
                              </Link>
                              {index < genres.length - 1 ? ', ' : ''}
                            </Fragment>
                          ))}
                        </div>
                      )}
                    </ErrorBoundary>
                    <ErrorBoundary fallback={null}>
                      <div className={styles.char}>
                        <span className={styles.charTitle}>{`${locale.NOTE_ARTICLE}:`}</span>
                        <span className={cn(styles.charContent, styles.still)}>{article}</span>
                      </div>
                    </ErrorBoundary>
                    <ErrorBoundary fallback={null}>
                      {arrangement?.title && (
                        <div className={styles.char}>
                          <span className={styles.charTitle}>{`${locale.NOTE_ARRANGEMENT}:`}</span>
                          <Link
                            href={`/category/arrangement-is-${arrangement.id}/apply/`}
                            className={styles.charContent}
                            prefetch={false}
                          >
                            {arrangement.title}
                          </Link>
                        </div>
                      )}
                    </ErrorBoundary>
                    <ErrorBoundary fallback={null}>
                      {instruments?.[0]?.title && (
                        <div className={styles.char}>
                          <span className={styles.charTitle}>{`${locale.NOTE_INSTRUMENTS}:`}</span>

                          {instruments.map((i, index) => (
                            <Fragment key={i.code}>
                              <Link
                                href={`/category/instruments-is-${i.code}/apply/`}
                                className={styles.charContent}
                                prefetch={false}
                              >
                                {i.title}
                              </Link>
                              {index < instruments.length - 1 ? ', ' : ''}
                            </Fragment>
                          ))}
                        </div>
                      )}
                    </ErrorBoundary>
                    <ErrorBoundary fallback={null}>
                      {countOtherArrangement ? (
                        <div className={styles.char}>
                          <span className={styles.charTitle}>{locale.NOTE_ARRANGEMENT_OTHER}</span>
                          <span
                            className={styles.charContent}
                            onClick={handleScrollToOtherArrangements}
                          >
                            {`${locale.WATCH_MORE} ${countOtherArrangement} 
                        ${getNounForm(countOtherArrangement, [
                          locale.SCORE_ONE || 'SCORE_ONE',
                          locale.SCORE_TWO || 'SCORE_TWO',
                          locale.SCORE_MANY || 'SCORE_MANY',
                        ])}`}
                          </span>
                        </div>
                      ) : null}
                    </ErrorBoundary>
                    <ErrorBoundary fallback={null}>
                      {signatureartist?.title ? (
                        <div className={styles.char}>
                          <span
                            className={styles.charTitle}
                          >{`${locale.NOTE_ARRANGER || 'NOTE_ARRANGER'}:`}</span>
                          <Link
                            href={signatureartist?.url || '/'}
                            className={styles.charContent}
                            prefetch={false}
                          >
                            {signatureartist.title}
                          </Link>
                        </div>
                      ) : null}
                    </ErrorBoundary>
                  </div>

                  <ErrorBoundary fallback={null}>
                    {mp3 && musicians && (
                      <Player musicians={musicians} title={title} mp3={mp3} locale={locale} />
                    )}
                  </ErrorBoundary>
                </div>
              </div>
            </div>
          </ErrorBoundary>
        </div>
        <VideoLesson
          locale={locale}
          title={`${he.decode(instruments?.at(0)?.title?.toLocaleLowerCase() || '')} ${musicians?.map((item) => he.decode(item.title || '')).join(', ')} - ${title}`}
        />

        <ErrorBoundary fallbackRender={() => null}>
          <AiPlayer locale={locale} data={data} />
        </ErrorBoundary>
        <ErrorBoundary fallbackRender={() => null}>
          <Description
            handleScrollToSimilar={handleScrollToSimilar}
            handleScrollToQuestions={handleScrollToQuestions}
            handleScrollToReviews={handleScrollToReviews}
            handleScrollToDownload={handleScrollToDownload}
            data={data}
            locale={locale}
            musiciansArrangement={musiciansArrangement}
            hasSimilarMusicians={Boolean(similarMusicians?.length)}
          />
        </ErrorBoundary>

        <ErrorBoundary fallbackRender={() => null}>
          {otherArrangementsIds && musicians && (
            <OtherArrangements
              musicians={musicians}
              title={title}
              ids={otherArrangementsIds}
              ref={otherArrangementsRef}
              locale={locale}
              otherArrangement={otherArrangement}
              data={data}
            />
          )}
        </ErrorBoundary>
        <ErrorBoundary fallbackRender={() => null}>
          {!!musicians && !!genres && (
            <SimilarMusicians
              ref={similarRef}
              similarMusicians={similarMusicians}
              data={data}
            />
          )}
        </ErrorBoundary>
        <ErrorBoundary fallbackRender={() => null}>
          <RecentlyViewed locale={locale} id={orderId} />
        </ErrorBoundary>
        <ErrorBoundary fallbackRender={() => null}>
          {musicians && data?.isempty === false && (
            <DownloadPreview
              musicians={musicians}
              title={title}
              ref={downloadPdfRef}
              locale={locale}
              orderId={orderId}
              data={data}
            />
          )}
        </ErrorBoundary>
        <ErrorBoundary 
          fallbackRender={() => null}
          onError={(error, errorInfo) => {
            console.error('Ошибка в компоненте ProductQuestions:', {
              error: error.message,
              stack: error.stack,
              errorInfo,
              noteId: data.id.toString(),
              questionsCount: questions?.length || 0,
            });
            
            // Отправляем ошибку в систему логирования
            if (typeof window !== 'undefined') {
              try {
                void fetch('/api-v2/send-log', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    level: 3,
                    msg: JSON.stringify({
                      error: 'Критическая ошибка в блоке "Вопросы о товаре"',
                      details: error.message,
                      noteId: data.id.toString(),
                      stack: error.stack,
                      componentStack: errorInfo.componentStack,
                    }),
                    href: window.location.href,
                    titlePage: document?.title,
                    userAgent: navigator?.userAgent,
                  }),
                });
              } catch (logError) {
                console.error('Ошибка отправки лога об ошибке ProductQuestions:', logError);
              }
            }
          }}
        >
          <ProductQuestions 
            ref={questionsRef}
            initialQuestions={questions}
            questionsPager={questionsPager}
            noteId={data.id.toString()} 
            currency={data.price?.BASE?.CURRENCY || 'USD'} 
            locale={locale}
          />
        </ErrorBoundary>
        <ErrorBoundary fallbackRender={() => null}>
          <ProductReviews 
            ref={reviewsRef}
            initialReviews={reviews}
            reviewsPager={reviewsPager}
            noteId={data.id.toString()} 
            currency={data.price?.BASE?.CURRENCY || 'USD'} 
            locale={locale}
            initialRatings={reviewRatings}
          />
        </ErrorBoundary>
        <ErrorBoundary fallbackRender={() => null}>
          <Faq
            locale={locale}
            instruments={instruments?.map((item) => item.title).join(', ')}
            arrangement={arrangement?.title}
            faqItems={faqItems}
          />
        </ErrorBoundary>
        <ErrorBoundary fallbackRender={() => null}>
          <Guarantees locale={locale} />
        </ErrorBoundary>
        <ErrorBoundary fallbackRender={() => null}>
          {FeatureToggles.subscribeBlock && <Subscribe />}
        </ErrorBoundary>
      </div>
    </>
  );
};

export { NotesCardContent };
